// Çalışma Saatleri — kartlı detay sekmesi (design-principles §4, §8.1):
// slate50 zemin; üstte KPI satırı (açık gün / kapalı gün / haftalık süre),
// altında "Haftalık Program" kartı. Günler kart içinde ince çizgiyle ayrılmış
// satırlar (iç kutu yok); kaydet kart başlığında.

import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, IconButton, Skeleton, Switch, Tooltip, Typography } from "@mui/material";
import { AccessTime as ClockIcon, ContentCopy as CopyIcon } from "@mui/icons-material";
import { COLORS, TYPO, CARD_PAD, CARD_GAP, CARD_PAGE_PAD, dashboardSectionSx } from "../../../styles/uiConstants";
import { useAlert } from "../../../context/AlertProvider";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { ActionButton, FormSelect } from "../../../components/atoms";
import { KpiTile, SectionCard } from "../../../components/molecules";
import {
  useGetClinicWorkingHoursMutation,
  useUpdateClinicWorkingHoursMutation,
} from "../../../store/clinicApi";
import { WEEKDAYS, TIME_OPTIONS } from "../constants";

const TIME_SELECT_OPTIONS = TIME_OPTIONS.map((t) => ({ value: t, label: t }));
const WEEKDAY_NAMES = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma"];
const cardSx = { ...dashboardSectionSx, display: "flex", flexDirection: "column" };

const minutesOf = (hhmm) => {
  const [h, m] = String(hhmm || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** 2610 dk → "43 sa 30 dk", 2580 → "43 sa" */
export function formatDuration(totalMinutes) {
  const total = Math.max(0, totalMinutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h && !m) return "0 sa";
  return [h ? `${h} sa` : null, m ? `${m} dk` : null].filter(Boolean).join(" ");
}

export default function WorkingHoursTab({ clinicId, token, palette }) {
  const { showSuccess, showError } = useAlert();
  const canUpdate = useHasPermission("admin-clinics:update-working-hours");

  const [getClinicWorkingHours, { data: whData }] = useGetClinicWorkingHoursMutation();
  const [updateClinicWorkingHours, { isLoading: isUpdating }] = useUpdateClinicWorkingHoursMutation();

  const [schedule, setSchedule] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (token && clinicId) {
      getClinicWorkingHours({ token, clinicId }).finally(() => setLoaded(true));
    }
  }, [token, clinicId, getClinicWorkingHours]);

  const clinic = whData?.data?.clinic;

  useEffect(() => {
    if (clinic) setSchedule(clinic.workingSchedule || []);
  }, [clinic]);

  const loading = !loaded;

  const handleSave = async () => {
    const invalid = schedule.find((s) => minutesOf(s.endTime) <= minutesOf(s.startTime));
    if (invalid) {
      showError(`${invalid.day}: kapanış saati açılıştan sonra olmalı`);
      return;
    }
    try {
      const res = await updateClinicWorkingHours({ token, clinicId, workingSchedule: schedule }).unwrap();
      if (res?.success || res?.status === "success") showSuccess("Çalışma saatleri güncellendi");
      else showError(res?.message || "Güncelleme başarısız");
    } catch (err) {
      showError(err?.data?.message || "Güncelleme sırasında hata oluştu");
    }
  };

  const toggleDay = (dayValue) => {
    if (!canUpdate) return;
    setSchedule((prev) =>
      prev.some((s) => s.day === dayValue)
        ? prev.filter((s) => s.day !== dayValue)
        : [...prev, { day: dayValue, startTime: "09:00", endTime: "18:00" }]
    );
  };

  const updateTime = (dayValue, field, value) => {
    setSchedule((prev) => prev.map((s) => (s.day === dayValue ? { ...s, [field]: value } : s)));
  };

  const copyToAll = (dayValue) => {
    const source = schedule.find((s) => s.day === dayValue);
    if (!canUpdate || !source) return;
    setSchedule((prev) => prev.map((s) => ({ ...s, startTime: source.startTime, endTime: source.endTime })));
    showSuccess("Saatler diğer açık günlere uygulandı");
  };

  const applyPreset = (preset) => {
    if (!canUpdate) return;
    if (preset === "weekdays") setSchedule(WEEKDAY_NAMES.map((d) => ({ day: d, startTime: "09:00", endTime: "18:00" })));
    else if (preset === "all-week") setSchedule(WEEKDAYS.map((d) => ({ day: d.value, startTime: "09:00", endTime: "18:00" })));
    else if (preset === "clear") setSchedule([]);
  };

  const stats = useMemo(() => {
    const minutes = schedule.reduce((sum, s) => sum + Math.max(0, minutesOf(s.endTime) - minutesOf(s.startTime)), 0);
    return { open: schedule.length, closed: WEEKDAYS.length - schedule.length, weekly: formatDuration(minutes) };
  }, [schedule]);

  return (
    <Box
      sx={{
        ...CARD_PAGE_PAD,
        bgcolor: COLORS.slate50,
        minHeight: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: CARD_GAP,
      }}
    >
      {/* KPI satırı */}
      <Box sx={{ display: "grid", gap: CARD_GAP, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" } }}>
        <KpiTile loading={loading} label="Açık gün" value={stats.open} hint="Haftada randevu alınabilen gün" />
        <KpiTile loading={loading} label="Kapalı gün" value={stats.closed} hint="Randevuya kapalı gün" />
        <KpiTile loading={loading} label="Haftalık süre" value={stats.weekly} hint="Açık günlerin toplam çalışma süresi" />
      </Box>

      <SectionCard
        title="Haftalık Program"
        subtitle="Açık günleri seçin, açılış ve kapanış saatlerini ayarlayın"
        icon={<ClockIcon sx={{ fontSize: 18 }} />}
        sx={cardSx}
        action={canUpdate && !loading ? (
          <ActionButton variant="save" compact palette={palette} loading={isUpdating} onClick={handleSave} />
        ) : null}
      >
        {/* Hızlı ayar */}
        {canUpdate && !loading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              px: CARD_PAD,
              py: 1.5,
              borderBottom: `1px solid ${COLORS.slate100}`,
            }}
          >
            <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, mr: 0.5 }}>Hızlı ayar</Typography>
            <PresetButton onClick={() => applyPreset("weekdays")}>Hafta içi 09:00–18:00</PresetButton>
            <PresetButton onClick={() => applyPreset("all-week")}>Her gün 09:00–18:00</PresetButton>
            <PresetButton onClick={() => applyPreset("clear")}>Tümünü kapat</PresetButton>
          </Box>
        )}

        {WEEKDAYS.map((day, i) => {
          const entry = schedule.find((s) => s.day === day.value);
          const isOpen = Boolean(entry);
          const last = i === WEEKDAYS.length - 1;
          const invalid = isOpen && minutesOf(entry.endTime) <= minutesOf(entry.startTime);
          return (
            <Box
              key={day.value}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: CARD_PAD,
                py: 1,
                minHeight: 52,
                flexWrap: "wrap",
                borderBottom: last ? "none" : `1px solid ${COLORS.slate100}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 150, flexShrink: 0 }}>
                {loading ? (
                  <Skeleton variant="rounded" width={34} height={18} sx={{ borderRadius: 9 }} />
                ) : (
                  <Switch
                    size="small"
                    checked={isOpen}
                    disabled={!canUpdate}
                    onChange={() => toggleDay(day.value)}
                    inputProps={{ "aria-label": `${day.value} açık` }}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: palette.primary },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: palette.primary },
                    }}
                  />
                )}
                <Typography sx={{ fontSize: TYPO.body, fontWeight: 500, color: isOpen ? COLORS.slate800 : COLORS.slate500 }}>
                  {day.value}
                </Typography>
              </Box>

              {loading ? (
                <Skeleton variant="text" width={160} height={24} />
              ) : isOpen ? (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TimeSelect
                      value={entry.startTime}
                      onChange={(v) => updateTime(day.value, "startTime", v)}
                      disabled={!canUpdate}
                      palette={palette}
                      label={`${day.value} açılış`}
                    />
                    <Typography sx={{ color: COLORS.slate400 }}>–</Typography>
                    <TimeSelect
                      value={entry.endTime}
                      onChange={(v) => updateTime(day.value, "endTime", v)}
                      disabled={!canUpdate}
                      palette={palette}
                      label={`${day.value} kapanış`}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: TYPO.caption,
                      color: invalid ? COLORS.errorDark : COLORS.slate500,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {invalid
                      ? "Kapanış açılıştan sonra olmalı"
                      : formatDuration(minutesOf(entry.endTime) - minutesOf(entry.startTime))}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {canUpdate && schedule.length > 1 && (
                    <Tooltip title="Bu saatleri diğer açık günlere uygula" arrow>
                      <IconButton size="small" onClick={() => copyToAll(day.value)} aria-label={`${day.value} saatlerini diğer günlere uygula`}>
                        <CopyIcon sx={{ fontSize: 16, color: COLORS.slate500 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate400 }}>Kapalı</Typography>
              )}
            </Box>
          );
        })}
      </SectionCard>
    </Box>
  );
}

function PresetButton({ onClick, children }) {
  return (
    <Button
      variant="outlined"
      size="small"
      onClick={onClick}
      sx={{
        textTransform: "none",
        fontSize: TYPO.caption,
        fontWeight: 500,
        px: 1.5,
        borderRadius: 2,
        color: COLORS.slate700,
        borderColor: COLORS.slate200,
        "&:hover": { borderColor: COLORS.slate300, bgcolor: COLORS.slate50 },
      }}
    >
      {children}
    </Button>
  );
}

function TimeSelect({ value, onChange, disabled, palette, label }) {
  return (
    <FormSelect
      palette={palette}
      value={value || "09:00"}
      onChange={onChange}
      options={TIME_SELECT_OPTIONS}
      placeholder={null}
      disabled={disabled}
      variant="standard"
      size="small"
      fullWidth={false}
      sx={{ width: 76 }}
      InputProps={{
        disableUnderline: disabled,
        sx: { fontSize: TYPO.body, fontVariantNumeric: "tabular-nums", "& .MuiSelect-select": { py: 0.25 } },
      }}
      SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 240 } } } }}
      inputProps={{ "aria-label": label }}
    />
  );
}
